import { MaterialCommunityIcons } from '@expo/vector-icons';
import { type LayoutChangeEvent, StyleSheet, View } from 'react-native';
import { Text, TouchableRipple } from 'react-native-paper';

import { NotificationDot } from '@/components/atoms/indicator';
import { colorSemantic, radii, rippleOnSurface, spacing, useAppTheme } from '@/theme';

const UNREAD_DOT_SIZE = 6;

interface FilterDropdownChipProps {
  readonly label: string;
  readonly value: string;
  readonly onPress: () => void;
  readonly selected?: boolean;
  readonly showDot?: boolean;
  readonly onLayout?: (event: LayoutChangeEvent) => void;
  readonly testID?: string;
}

const styles = StyleSheet.create({
  wrapper: {
    alignSelf: 'flex-start',
  },
  // The 6px dot straddles the chip's top-left corner, 2px left and 2px above
  // the edge. A larger overhang reads as clipped by the chip border.
  dot: {
    left: -2,
    position: 'absolute',
    top: -2,
    zIndex: 1,
  },
  chip: {
    alignSelf: 'flex-start',
    borderWidth: 1,
    borderRadius: radii.sm,
    height: 32,
    justifyContent: 'center',
    overflow: 'hidden',
  },
  content: {
    alignItems: 'center',
    columnGap: spacing[2],
    flexDirection: 'row',
    paddingLeft: spacing[4],
    paddingRight: spacing[2],
  },
  text: {
    fontSize: 14,
    fontWeight: '500',
    includeFontPadding: false,
    letterSpacing: 0.1,
    lineHeight: 20,
  },
});

export const FilterDropdownChip = ({
  label,
  value,
  onPress,
  selected = false,
  showDot = false,
  onLayout,
  testID,
}: FilterDropdownChipProps) => {
  const { colors } = useAppTheme();
  const chipText = value.length > 0 ? `${label}: ${value}` : label;
  const borderColor = selected ? colors.secondary : colors.outline;
  const backgroundColor = selected ? colors.secondaryContainer : 'transparent';

  return (
    <View onLayout={onLayout} style={styles.wrapper}>
      {showDot && (
        <View style={styles.dot}>
          <NotificationDot
            color={colorSemantic.colorBgErrorStrong}
            size={UNREAD_DOT_SIZE}
            testID={testID ? `${testID}-dot` : undefined}
          />
        </View>
      )}
      <TouchableRipple
        accessibilityRole="button"
        borderless={false}
        rippleColor={rippleOnSurface}
        style={[styles.chip, { borderColor, backgroundColor }]}
        testID={testID}
        onPress={onPress}>
        <View style={styles.content}>
          <Text style={[styles.text, { color: colors.textNdPrimary }]}>{chipText}</Text>
          <MaterialCommunityIcons
            color={colors.iconNdPrimary}
            name="chevron-down"
            size={18}
            testID="filter-dropdown-chip-icon"
          />
        </View>
      </TouchableRipple>
    </View>
  );
};
